import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchSubscription, getCachedSubscription } from '../services/SubscriptionService';
// v1.12.117 — wrapper avec fallback localStorage sur web (expo-secure-store
// résout null sur web → la session ne persistait pas après refresh).
import * as SecureStore from '../utils/secureStore';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import { AWS_CONFIG, IS_AWS_CONFIGURED } from '../config/aws_config';

const AuthContext = createContext(null);

/* ─── Demo user ──────────────────────────────────────────── */
const DEMO_USER = {
  id: 'demo_001', name: 'Sofia Dupont', email: 'sofia.dupont@gmail.com',
  avatar: null, initials: 'SD', provider: 'demo', plan: 'premium',
  city: 'Paris', temp: '18°C',
  stats: { looks: 24, commandes: 8, pieces: 32 },
  preferences: {
    styles: ['Chic', 'Soirée'], colors: ['Noir', 'Or'],
    marques: ['Sézane', 'Zalando'], budget: '100-200',
  },
};

function initials(name = '', user = null) {
  // Si user fourni avec firstName/lastName, on les privilégie
  if (user) {
    const fn = (user.firstName || '').trim();
    const ln = (user.lastName  || '').trim();
    if (fn && ln) return (fn[0] + ln[0]).toUpperCase();
    if (fn) return fn.slice(0, 2).toUpperCase();
    if (ln) return ln.slice(0, 2).toUpperCase();
  }
  // Si "name" ressemble à un email, on prend juste la 1ère lettre de la partie locale
  if (name && name.includes('@')) {
    const local = name.split('@')[0];
    if (local && local[0]) return local[0].toUpperCase();
    return 'U';
  }
  const p = (name || '').trim().split(' ').filter(Boolean);
  if (p.length >= 2) return (p[0][0] + p[1][0]).toUpperCase();
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return 'U';
}

/* ─── fetch avec timeout ─────────────────────────────────
   v1.13.x — FIX FREEZE : les fetch React Native n'ont PAS de timeout par
   défaut (≠ navigateur). Au lancement (restauration de session / refresh token
   / /profile), un réseau froid ou un token périmé pouvait faire HANG l'appel à
   l'infini → splash bloqué / Home figé (notamment après réinstall : token
   Keychain périmé + cache AsyncStorage vide). On borne donc ces appels. */
async function fetchWithTimeout(url, options = {}, ms = 12000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } finally {
    clearTimeout(id);
  }
}

/* ─── Cognito REST helper ───────────────────────────────── */
async function cognitoCall(target, body) {
  const res = await fetchWithTimeout(
    `https://cognito-idp.${AWS_CONFIG.region}.amazonaws.com/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
    },
    12000
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.__type || `Cognito error ${res.status}`);
  return data;
}

function decodeJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? b64 + '='.repeat(4 - b64.length % 4) : b64;
    return JSON.parse(atob(pad));
  } catch { return {}; }
}

/**
 * Normalise un code locale en BCP-47 court (fr, en, es, de, it, pt, ar, etc.)
 * Apple peut retourner "fr_FR" ou un array, Google "fr-FR" ou "fr".
 * Exemples :
 *   "fr-FR"   → "fr"
 *   "fr_FR"   → "fr"
 *   ["fr-FR"] → "fr"
 *   "FR"      → null   (pas une locale, c'est un pays)
 */
function normalizeLocale(input) {
  if (!input) return null;
  if (Array.isArray(input)) input = input[0];
  if (typeof input !== 'string') return null;
  // Normaliser : "fr_FR" / "fr-FR" → "fr"
  const m = input.toLowerCase().match(/^([a-z]{2,3})(?:[-_]([a-z]{2,4}))?/);
  if (!m) return null;
  const supported = ['fr', 'en', 'es', 'de', 'it', 'pt', 'nl', 'ar'];
  if (!supported.includes(m[1])) return null;
  return m[1];
}

// v1.12.799 — Purge les caches non-essentiels pour libérer le quota localStorage web.
// Sur web RN, localStorage = ~5MB partagés entre TOUTES les clés. Sans purge,
// les tokens auth peuvent échouer à sauvegarder → user reste en 401.
async function purgeNonEssentialStorage() {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.localStorage) return;
  try {
    // v1.12.809 — Préserver les snapshots user-data utilisés par d'autres écrans :
    //   outfit_looks_ia_<uid>     → Looks IA persistés par LooksScreen pour Calendar
    //   outfit_today_recommendation_<uid> → Tenue du jour pour Calendar
    //   outfit_today_options_<uid>        → Top 5 alternatives pour Calendar régénérer
    //   calendar_snapshots_<uid>          → Snapshots looks pour cellules calendrier
    const ESSENTIAL_PREFIXES = [
      'outfit_access', 'outfit_id', 'outfit_refresh', 'outfit_provider', 'outfit_user_',
      'outfit_looks_ia_',       // snapshot AI looks (Calendar dépend de ça)
      'outfit_today_recommendation_',
      'outfit_today_options_',
      'calendar_snapshots_',
    ];
    const PURGE_PATTERNS = [
      // /^outfit_looks_/ retiré (v1.12.809) car matchait outfit_looks_ia_ utilisé par Calendar
      /^outfit_styliste_/,
      /^outfit_favorites_v/,
      /^outfit_tryon_/,
      /^outfit_catalog_/,
      /^outfit_ai_/,
      /^outfit_stylist_/,
      /^outfit_trending_/,
      /^outfit_dynamic_/,
      /^outfit_color_bank_/,
      /^outfit_material_bank_/,
      /^outfit_style_bank_/,
      /^outfit_recent_/,
      /_aiMetadataBackend_/,
    ];
    const keys = Object.keys(window.localStorage);
    let purged = 0;
    for (const k of keys) {
      if (ESSENTIAL_PREFIXES.some(p => k.startsWith(p))) continue;
      if (PURGE_PATTERNS.some(re => re.test(k))) {
        window.localStorage.removeItem(k);
        purged++;
      }
    }
    if (purged > 0) console.log(`[AUTH] purgeNonEssentialStorage : ${purged} keys removed`);
  } catch (e) { console.warn('[AUTH] purgeNonEssentialStorage failed:', e?.message); }
}

async function saveTokens(auth, provider) {
  // Supporter les deux formats : Cognito (PascalCase) et OAuth2 (snake_case)
  const access  = auth.AccessToken  || auth.access_token  || '';
  const id      = auth.IdToken      || auth.id_token      || '';
  const refresh = auth.RefreshToken || auth.refresh_token || '';
  // v1.12.799 — purge cache avant save pour éviter QuotaExceeded sur web
  await purgeNonEssentialStorage();
  // ═══ FIX iOS 26 ═══
  // expo-secure-store throw NSException sur valeur vide / non-string,
  // qui devient fatale via RCTExceptionsManager → SIGABRT.
  // On wrappe chaque appel en try/catch et on skip les valeurs invalides.
  try {
    if (typeof access === 'string' && access.length > 0) {
      await SecureStore.setItemAsync('outfit_access', access);
    }
  } catch (e) { console.warn('[AUTH] saveTokens access failed:', e?.message); }
  try {
    if (typeof id === 'string' && id.length > 0) {
      await SecureStore.setItemAsync('outfit_id', id);
    }
  } catch (e) { console.warn('[AUTH] saveTokens id failed:', e?.message); }
  try {
    if (typeof refresh === 'string' && refresh.length > 0) {
      await SecureStore.setItemAsync('outfit_refresh', refresh);
    }
  } catch (e) { console.warn('[AUTH] saveTokens refresh failed:', e?.message); }
  try {
    if (provider && typeof provider === 'string') {
      await SecureStore.setItemAsync('outfit_provider', provider);
    }
  } catch (e) { console.warn('[AUTH] saveTokens provider failed:', e?.message); }
  // v1.13.18 — Marqueur d'install AsyncStorage (vidé à la désinstallation, ≠ Keychain
  // qui survit). Permet à l'effet de restauration de distinguer un relancement normal
  // d'une réinstallation et d'éviter le freeze Home « à froid ». Cf. FIX freeze réinstallation.
  try {
    const AS = (await import('@react-native-async-storage/async-storage')).default;
    await AS.setItem('outfit_install_marker', '1');
  } catch (_) {}
}
async function clearTokens() {
  // v1.12.624 — Log stack trace pour identifier qui appelle clearTokens
  // (bug "déconnexion brusque" après Profil → Accueil). À retirer une fois
  // le coupable identifié.
  try {
    console.warn('[AUTH] 🔍 clearTokens called from:', new Error('trace').stack?.split('\n').slice(1, 6).join('\n'));
  } catch {}
  var _keys = ['outfit_access','outfit_id','outfit_refresh','outfit_provider'];
  for (var _ki = 0; _ki < _keys.length; _ki++) {
    await SecureStore.deleteItemAsync(_keys[_ki]).catch(function() {});
  }
  // v1.12.268 — Web: SecureStore est un stub. On clear aussi localStorage + AsyncStorage
  try {
    if (typeof localStorage !== 'undefined') {
      _keys.forEach(function(k) { try { localStorage.removeItem(k); } catch(_) {} });
      try { localStorage.removeItem('outfit_user'); } catch(_) {}
      try { localStorage.removeItem('mytenue_profile_cache'); } catch(_) {}
      // v1.12.629 — Clear legacy favoris globaux (contamination cross-account)
      try { localStorage.removeItem('outfit_favorites_v1'); } catch(_) {}
    }
  } catch(_) {}
  try {
    var AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    var moreKeys = ['outfit_user', 'mytenue_profile_cache', 'outfit_favorites_v1'];
    for (var i = 0; i < moreKeys.length; i++) {
      await AsyncStorage.removeItem(moreKeys[i]).catch(function() {});
    }
  } catch(_) {}
}

/* ─── Cache profil local ─────────────────────────────────────
 * Apple Sign-In ne renvoie firstName/lastName/email qu'au TOUT premier
 * sign-in. Au re-launch de l'app, le JWT Apple stocké en SecureStore ne
 * contient PAS ces champs → buildUser() retourne un user sans nom ni
 * email pendant les ~2-3s nécessaires au fetch DB. L'utilisateur voit
 * "Mon profil" / pas d'email pendant ce temps.
 *
 * Solution : on persiste un snapshot local des infos profil après chaque
 * hydratation DB réussie. Au démarrage on restaure ces champs immédiatement
 * (avant même le fetch DB) → pas de flash, l'écran Profil affiche
 * directement le bon nom.
 */
async function saveProfileCache(userId, profile) {
  if (!userId || !profile) return;
  try {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    const cache = {
      firstName: profile.firstName || '',
      lastName:  profile.lastName  || '',
      name:      profile.name      || '',
      email:     profile.email     || '',
      avatar:    profile.avatar    || null,
    };
    await AsyncStorage.setItem(`outfit_profile_cache_${userId}`, JSON.stringify(cache));
  } catch (_) {}
}
async function loadProfileCache(userId) {
  if (!userId) return null;
  try {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    const raw = await AsyncStorage.getItem(`outfit_profile_cache_${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}
async function clearProfileCache(userId) {
  if (!userId) return;
  try {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    await AsyncStorage.removeItem(`outfit_profile_cache_${userId}`);
  } catch (_) {}
}

/* ─── API helper — upsert profile in DB ─────────────────── */
function formatMemberSince(isoDate) {
  if (!isoDate) return 'Récemment';
  const d = new Date(isoDate);
  const months = ['Janvier','Février','Mars','Avril','Mai','Juin',
                  'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

async function upsertProfileInDB(accessToken, userInfo) {
  if (!IS_AWS_CONFIGURED) return;
  try {
    // ═══ FIX 2026-04-26 : NE PAS envoyer de chaînes vides ═══
    // Apple ne donne firstName/lastName/email qu'au TOUT premier sign-in.
    // Aux re-login, ces champs sont vides. Si on les envoyait à PUT /profile,
    // le backend (qui pousse tout val !== null && !== undefined) écraserait
    // les vraies valeurs en DB par des chaînes vides → user perd son nom au
    // 2e launch.
    // Solution : on n'inclut un champ que s'il a une vraie valeur. Le backend
    // sait déjà préserver les colonnes non envoyées (UPDATE partiel).
    const body = {
      provider: userInfo.provider || 'google',
      city:     'Paris',
    };
    if (userInfo.name       && userInfo.name.trim())       body.name       = userInfo.name.trim();
    if (userInfo.first_name && userInfo.first_name.trim()) body.first_name = userInfo.first_name.trim();
    if (userInfo.last_name  && userInfo.last_name.trim())  body.last_name  = userInfo.last_name.trim();
    if (userInfo.email      && userInfo.email.trim())      body.email      = userInfo.email.trim();
    if (userInfo.avatar_url) body.avatar_url = userInfo.avatar_url;
    // Only include gender if detected
    if (userInfo.gender) body.gender = userInfo.gender;
    // Préférence de langue depuis OAuth provider (Google claim ou Apple locale système)
    if (userInfo.preferred_locale) body.preferred_locale = userInfo.preferred_locale;

    const res = await fetch(`${AWS_CONFIG.apiUrl}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return data;
  } catch (e) {
    console.warn('Profile upsert failed:', e.message);
  }
}

/* ─── Cognito Hosted UI (Google OAuth) ──────────────────── */
async function exchangeCodeForTokens(code, redirectUri) {
  const domain = AWS_CONFIG.oauth?.domain;
  if (!domain) throw new Error('Cognito domain not configured');

  const res = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'grant_type=authorization_code' +
      '&client_id=' + encodeURIComponent(AWS_CONFIG.userPoolClientId) +
      '&code=' + encodeURIComponent(code) +
      '&redirect_uri=' + encodeURIComponent(redirectUri),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error_description || data.error);
  return data; // { access_token, id_token, refresh_token }
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  /* ── Sync user vers UserContextService (cache synchrone pour services) ── */
  // Permet aux services (matching, scoring) d'accéder à user.age/gender/morpho
  // sans passer par React hooks (qui ne marchent que dans les components).
  useEffect(() => {
    try {
      const m = require('../services/UserContextService');
      if (m && typeof m.setCachedUser === 'function') {
        m.setCachedUser(user);
      }
    } catch (_) {}
    // Émettre la locale préférée pour I18nProvider (différé pour éviter un
    // appel pendant que I18nProvider n'a pas encore subscribed)
    if (user?.preferred_locale) {
      const t = setTimeout(() => {
        try {
          const RN = require('react-native');
          const em = RN && RN.DeviceEventEmitter;
          if (em && typeof em.emit === 'function') {
            em.emit('user_preferred_locale', user.preferred_locale);
          }
        } catch (_) {}
      }, 100);
      return () => clearTimeout(t);
    }
  }, [
    user?.id, user?.birthDate, user?.gender, user?.tier,
    user?.height_cm, user?.weight_kg, user?.silhouette,
    user?.clothing_sizes, user?.preferred_locale,
  ]);

  /* ── Persister le cache profil local (pour Apple re-launch) ── */
  // À chaque fois que user a des infos non vides (post-hydratation DB),
  // on les sauve en AsyncStorage. Au prochain démarrage de l'app, ces
  // valeurs sont injectées dans le user AVANT le fetch DB → pas de flash
  // "Mon profil" pour les Apple users dont le JWT au re-launch est vide.
  useEffect(() => {
    if (!user?.id) return;
    // Ne persister que si on a au moins une vraie info (sinon on overwrite
    // un cache valide avec du vide, ce qui annulerait l'effet recherché)
    const hasInfo = (user.firstName && user.firstName.trim())
                 || (user.lastName  && user.lastName.trim())
                 || (user.name      && user.name.trim() && user.name !== 'Utilisateur')
                 || (user.email     && user.email.trim());
    if (!hasInfo) return;
    saveProfileCache(user.id, user).catch(() => {});
  }, [user?.id, user?.firstName, user?.lastName, user?.name, user?.email, user?.avatar]);

  /* ── Reset compteur IA à minuit (vérification toutes les 5 min) ── */
  // Garantit le reset à 00:00 même si l'app reste ouverte toute la nuit
  useEffect(() => {
    if (!user?.id) return;
    const checkAndReset = async () => {
      try {
        const { resetAIUsageIfNewDay } = require('../services/PremiumService');
        await resetAIUsageIfNewDay(user.id);
      } catch(e) {}
    };
    checkAndReset(); // check immédiat
    const interval = setInterval(checkAndReset, 5 * 60 * 1000); // toutes les 5min
    return () => clearInterval(interval);
  }, [user?.id]);

  /* ── Identifier RevenueCat avec email + attributs (v1.7.53) ─────── */
  // Avant : RevenueCat ne recevait que le userId Cognito → dans le dashboard
  //   les customers apparaissaient sans email ni nom, juste un ID hexa.
  //   Pire : si initIAP était appelé avant le login, RC restait en mode
  //   anonymous ($RCAnonymousID:xxx) → les achats étaient liés à un
  //   ID anonyme au lieu du Cognito sub.
  // Maintenant : dès qu'un user est connecté + a un email, on l'identifie
  //   à RevenueCat avec setEmail() + setAttributes() → email/country/version
  //   visibles dans le dashboard RC. Idempotent et non-bloquant.
  useEffect(() => {
    if (!user?.id || !user?.email) return;
    let cancelled = false;
    (async () => {
      try {
        const { identifyUser } = require('../services/IAPService');
        await identifyUser(user.id, {
          email:       user.email,
          displayName: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || null,
          country:     user.country || 'FR',
          locale:      user.lang || 'fr',
        });
        if (cancelled) return;
        console.log(`[AUTH] ✓ identifyUser RC done for ${user.email}`);
      } catch (e) {
        // Non-bloquant : si RC plante, on continue. Le webhook /payment/webhook-revenuecat
        // recevra de toutes façons l'app_user_id et pourra matcher en DB.
        console.warn('[AUTH] identifyUser RC failed (non-bloquant):', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.email, user?.name]);

  /* ── Restore session on startup ──────────────────────── */
  useEffect(() => {
    (async () => {
      if (!IS_AWS_CONFIGURED) { setLoading(false); return; }
      try {
        // ═══ FIX iOS 26 ═══
        // expo-secure-store peut throw NSException si Keychain dans état
        // corrompu post-migration. Tous les getItemAsync sont wrappés.
        const access = await SecureStore.getItemAsync('outfit_access').catch(e => {
          console.warn('[AUTH] read outfit_access failed:', e?.message);
          return null;
        });
        if (!access) { setLoading(false); return; }

        const payload = decodeJwt(access);
        const now     = Math.floor(Date.now() / 1000);

        // ═══ FIX freeze réinstallation (iOS) — v1.13.18 ═══
        // Le Keychain iOS SURVIT à la désinstallation, mais AsyncStorage est vidé.
        // Au re-install, l'app auto-restaure la session depuis le token Keychain et
        // monte Home « à froid » (tous les caches AsyncStorage vides : profil, tier,
        // styliste, catalogue, météo…) → l'écran d'accueil GÈLE. Le contournement
        // manuel de l'utilisateur = forcer un nouveau login. On l'automatise : si on a
        // un token Keychain MAIS aucune donnée AsyncStorage liée au compte → c'est une
        // réinstallation → on purge les tokens et on route vers Login (réhydratation
        // propre, identique à un login normal qui, lui, ne gèle pas).
        // Migration-safe : les users déjà connectés possèdent déjà un cache profil /
        // flag onboarding / client_id GA → jamais déconnectés à tort au déploiement.
        if (Platform.OS !== 'web' && payload?.sub) {
          try {
            const AS = (await import('@react-native-async-storage/async-storage')).default;
            // ⚠️ NE PAS utiliser outfit_ga_client_id ni d'autres clés écrites au
            // tout début du boot (analytics) : elles peuvent être posées AVANT que
            // cet effet ne lise les marqueurs → fausse « données présentes » et
            // empêche la détection de réinstall. On ne se fie qu'aux clés écrites
            // APRÈS un usage réel (cache profil chargé, onboarding terminé, login).
            const [profCache, onbFlag, marker] = await Promise.all([
              AS.getItem(`outfit_profile_cache_${payload.sub}`).catch(() => null),
              AS.getItem(`outfit_onboarding_done_${payload.sub}`).catch(() => null),
              AS.getItem('outfit_install_marker').catch(() => null),
            ]);
            const hasLocalData = !!profCache || !!onbFlag || !!marker;
            if (!hasLocalData) {
              console.warn('[AUTH] réinstallation détectée (token Keychain, AsyncStorage vierge) → login propre forcé');
              await clearTokens().catch(() => {});
              setLoading(false);
              return;
            }
            // Poser le marqueur pour les prochains lancements (migration douce).
            if (!marker) { try { await AS.setItem('outfit_install_marker', '1'); } catch (_) {} }
          } catch (_) {}
        }

        if (payload.exp && payload.exp < now) {
          // Token expiré → tenter refresh
          const refresh = await SecureStore.getItemAsync('outfit_refresh').catch(() => null);
          if (!refresh) { await clearTokens(); setLoading(false); return; }
          try {
            const r = await cognitoCall('InitiateAuth', {
              AuthFlow:       'REFRESH_TOKEN_AUTH',
              ClientId:       AWS_CONFIG.userPoolClientId,
              AuthParameters: { REFRESH_TOKEN: refresh },
            });
            await saveTokens(r.AuthenticationResult);
            const newPayload = decodeJwt(r.AuthenticationResult.AccessToken);
            const savedProvider = await SecureStore.getItemAsync('outfit_provider').catch(() => null);
            const refreshedUser = buildUser(newPayload, r.AuthenticationResult.AccessToken);
            if (savedProvider) refreshedUser.provider = savedProvider;

            // ═══ FIX 2026-04 : cache profil pour refresh path aussi ═══
            try {
              const cached = await loadProfileCache(refreshedUser.id);
              if (cached) {
                if (!refreshedUser.firstName && cached.firstName) refreshedUser.firstName = cached.firstName;
                if (!refreshedUser.lastName  && cached.lastName)  refreshedUser.lastName  = cached.lastName;
                if (!refreshedUser.name      || refreshedUser.name === 'Utilisateur') {
                  if (cached.name) refreshedUser.name = cached.name;
                }
                if (!refreshedUser.email     && cached.email)     refreshedUser.email     = cached.email;
                if (!refreshedUser.avatar    && cached.avatar)    refreshedUser.avatar    = cached.avatar;
                if (cached.firstName || cached.name) {
                  refreshedUser.initials = initials(cached.name || cached.firstName);
                }
              }
            } catch (_) {}

            setUser(refreshedUser);
            loadSubscriptionForUser(refreshedUser.id, r.AuthenticationResult.AccessToken, setUser);
          } catch (refreshErr) {
            console.warn('Refresh failed:', refreshErr?.message || refreshErr);
            await clearTokens();
          }
        } else {
          // Token encore valide → restaurer la session
          const savedProvider = await SecureStore.getItemAsync('outfit_provider').catch(() => null);
          const restoredUser  = buildUser(payload, access);
          if (savedProvider) restoredUser.provider = savedProvider;

          // ═══ FIX 2026-04 : injecter le cache profil avant setUser ═══
          // Apple ne met pas firstName/lastName/email dans le JWT au re-launch.
          // On charge les valeurs persistées localement (de la session précédente)
          // pour éviter le flash "Mon profil" pendant le fetch DB.
          try {
            const cached = await loadProfileCache(restoredUser.id);
            if (cached) {
              if (!restoredUser.firstName && cached.firstName) restoredUser.firstName = cached.firstName;
              if (!restoredUser.lastName  && cached.lastName)  restoredUser.lastName  = cached.lastName;
              if (!restoredUser.name      || restoredUser.name === 'Utilisateur') {
                if (cached.name) restoredUser.name = cached.name;
              }
              if (!restoredUser.email     && cached.email)     restoredUser.email     = cached.email;
              if (!restoredUser.avatar    && cached.avatar)    restoredUser.avatar    = cached.avatar;
              if (cached.firstName || cached.name) {
                restoredUser.initials = initials(cached.name || cached.firstName);
              }
            }
          } catch (_) {}

          setUser(restoredUser);
          loadSubscriptionForUser(restoredUser.id, access, setUser);
        }
      } catch (e) {
        console.warn('Session restore error:', e?.message || e);
        await clearTokens().catch(() => {});
      }
      finally  { setLoading(false); }
    })();
  }, []);

  // Retourne un token valide — rafraîchit automatiquement si expiré
  async function getValidToken() {
    try {
      // ═══ FIX 2026-04 : retourner l'idToken (pas l'accessToken) ═══
      // L'idToken contient les claims JWT (email, name, picture) requis
      // par le backend Lambda pour synchroniser le profil.
      // L'accessToken ne contient que { sub, scope, exp }.
      const idTok     = await SecureStore.getItemAsync('outfit_id');
      const access    = await SecureStore.getItemAsync('outfit_access');
      // Si on a l'idToken ET qu'il est encore valide → le retourner
      if (idTok) {
        try {
          const idPayload = decodeJwt(idTok);
          const now = Math.floor(Date.now() / 1000);
          if (idPayload.exp && idPayload.exp > now + 60) {
            return idTok;
          }
        } catch {}
      }
      // Sinon on vérifie l'accessToken pour déclencher un refresh si expiré
      if (!access) return null;
      const payload = decodeJwt(access);
      const now     = Math.floor(Date.now() / 1000);
      // Si accessToken encore valide mais idToken absent/périmé → renvoyer idToken s'il existe
      if (payload.exp && payload.exp > now + 60) {
        return idTok || access;
      }
      // Tokens expirés → rafraîchir (Cognito renvoie idToken + accessToken)
      const refresh = await SecureStore.getItemAsync('outfit_refresh');
      if (!refresh) return null;
      const r = await cognitoCall('InitiateAuth', {
        AuthFlow:       'REFRESH_TOKEN_AUTH',
        ClientId:       AWS_CONFIG.userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: refresh },
      });
      await saveTokens(r.AuthenticationResult);
      const newId     = r.AuthenticationResult.IdToken;
      const newAccess = r.AuthenticationResult.AccessToken;
      const newPayload = decodeJwt(newAccess);
      // v1.12.244 — FIX critique : ne pas écraser firstName/lastName/avatar
      // quand on rafraîchit le token. L'AccessToken JWT n'a que { sub, exp, scope }
      // donc buildUser retourne 'Utilisateur'. On MERGE avec l'user actuel +
      // cache profil pour préserver les infos.
      const tokenUser = buildUser(newPayload, newId || newAccess);
      setUser(prev => {
        const base = prev || {};
        const merged = {
          ...tokenUser,
          // Ces champs sont absents du JWT refresh → on garde ceux en mémoire
          firstName: base.firstName || tokenUser.firstName,
          lastName:  base.lastName  || tokenUser.lastName,
          email:     base.email     || tokenUser.email,
          name:      (base.name && base.name !== 'Utilisateur') ? base.name : tokenUser.name,
          avatar:    base.avatar    || tokenUser.avatar,
          gender:    base.gender    || tokenUser.gender,
          tier:      base.tier      || tokenUser.tier,
          provider:  base.provider  || tokenUser.provider,
          initials:  base.initials  || tokenUser.initials,
          country:   base.country   || tokenUser.country,
          lang:      base.lang      || tokenUser.lang,
          // Toujours utiliser les nouveaux tokens
          accessToken: newAccess,
          idToken:     newId,
        };
        return merged;
      });
      // Filet en plus : essayer de récupérer le cache profil de manière async
      // (au cas où l'user était null en mémoire — re-mount React, etc.)
      try {
        const cached = await loadProfileCache(tokenUser.id);
        if (cached) {
          setUser(prev => ({
            ...(prev || tokenUser),
            firstName: prev?.firstName || cached.firstName || prev?.firstName,
            lastName:  prev?.lastName  || cached.lastName  || prev?.lastName,
            email:     prev?.email     || cached.email     || prev?.email,
            name: (prev?.name && prev.name !== 'Utilisateur') ? prev.name : (cached.name || prev?.name),
            avatar:    prev?.avatar    || cached.avatar    || prev?.avatar,
          }));
        }
      } catch {}
      return newId || newAccess;
    } catch (e) {
      console.warn('getValidToken:', e.message);
      return user?.accessToken || null;
    }
  }

  function buildUser(payload, accessToken) {
    const providerName = payload.identities
      ? (typeof payload.identities === 'string'
          ? JSON.parse(payload.identities)[0]?.providerName
          : payload.identities[0]?.providerName)?.toLowerCase()
      : 'cognito';
    return {
      id:          payload.sub,
      name:        payload.name || payload['cognito:username'] || payload.email || 'Utilisateur',
      firstName:   payload.given_name  || '',
      lastName:    payload.family_name || '',
      email:       payload.email || '',
      avatar:      payload.picture || null,
      initials:    initials(payload.name || payload.given_name || ''),
      provider:    providerName || 'cognito',
      gender:      payload.gender || null,
      // birthDate : Google/Apple peuvent renvoyer 'birthdate' via scope
      // (Google 'birthday' scope / Apple 'email name' seulement, pas de birthdate).
      // On prend ce qui est dispo dans le JWT, sinon null → formulaire.
      birthDate:   payload.birthdate || payload.birthday || null,
      plan:        'free',
      city:        'Paris',
      temp:        '18°C',
      accessToken,
      stats:       { looks: 0, commandes: 0, pieces: 0 },
      preferences: DEMO_USER.preferences,
    };
  }

  // Charger et synchroniser l'abonnement depuis RDS après login
  async function loadSubscriptionForUser(userId, accessToken, setter) {
    // ═══ RESET COMPTEUR AI SI NOUVEAU JOUR ═══
    // Appelé à chaque démarrage de session → garantit que le compteur IA
    // est à 0 dès 00:00 chaque jour pour tous les abonnements.
    try {
      const { resetAIUsageIfNewDay } = require('../services/PremiumService');
      await resetAIUsageIfNewDay(userId);
    } catch(e) {}

    try {
      // D'abord essayer le cache local pour une réponse instantanée
      const cached = await getCachedSubscription(userId);
      if (cached && cached.status === 'active') {
        const cachedPlan = cached.period || 'monthly';
        setter(prev => prev ? { ...prev, plan: cachedPlan } : prev);
        console.log('[AUTH] Subscription from cache:', cachedPlan, 'user:', userId);
      }

      // Charger le TIER depuis /profile (source de vérité)
      let profileLoaded = false;
      try {
        const profRes = await fetchWithTimeout(`${AWS_CONFIG.apiUrl}/profile`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        }, 12000);
        if (profRes.ok) {
          const prof = await profRes.json();
          // v1.12.966 — Géo GA depuis le backend (pays/ville résolus via IP au login).
          // N'est envoyée à GA que si le consentement le permet (géré dans AnalyticsService).
          try {
            const { setUserGeo } = require('../services/AnalyticsService');
            // v1.13.12 — FIX géo GA mobile : le backend renvoie last_login_country/
            // last_login_city (SELECT * profiles), PAS country_code/city/region.
            // L'ancien mapping donnait undefined → aucune géo envoyée à GA sur mobile.
            // v1.13.28 — FIX "city toujours Paris" : la city backend vient de l'IP
            // (les IP mobiles FR géolocalisent toutes vers le POP opérateur = Paris).
            // On NE l'envoie PLUS à GA → la ville vient UNIQUEMENT du GPS (HomeScreen,
            // reverse-geocode précis). On ne garde que pays/région (fiables).
            setUserGeo({
              country: prof.last_login_country || prof.country_code || prof.country || null,
              region:  prof.last_login_region || prof.region || null,
            });
          } catch (_) {}
          // ═══ v1.12.608 : backend = source de vérité (fix tier conflict éternel) ═══
          // AVANT (FIX 2026-04) : on gardait toujours le tier local s'il était plus
          //   élevé que le backend, et on auto-resyncait via /set-tier. Bug : si un
          //   webhook avait légitimement baissé le tier (sub annulée + expirée,
          //   admin reset, downgrade volontaire), le cache local restait bloqué et
          //   ré-injectait l'ancien tier en DB à chaque reload — irréversible.
          //
          // MAINTENANT : backend gagne par défaut. On ne garde le local que si :
          //   (a) le backend dit 'free' MAIS a un signal de "période payée encore
          //       active" (subscription_period non nul ET pas cancelled ET
          //       premium_expires_at futur) — cas d'un webhook /set-tier qui a
          //       raté juste après un checkout réussi.
          //   (b) Dans ce cas, on tente UN resync. Sinon on purge le cache local.
          const TIER_RANK = { free: 0, basic: 1, premium: 2, gold: 3 };
          const backendTier = prof?.tier || 'free';
          let storedTier = null;
          let AsyncStorageRef = null;
          try {
            AsyncStorageRef = (await import('@react-native-async-storage/async-storage')).default;
            storedTier = await AsyncStorageRef.getItem(`outfit_tier_${userId}`);
          } catch {}

          // Détecter si le backend a un signal de "sub active" (= raison de croire
          // le local). Sinon, son 'free' est légitime (jamais souscrit, ou
          // cancelled+expiré, ou admin reset).
          const expAtRaw = prof?.premium_expires_at;
          const expFuture = expAtRaw ? (new Date(expAtRaw).getTime() > Date.now()) : false;
          const backendHasActiveSub =
            !!prof?.subscription_period &&
            !prof?.subscription_cancelled &&
            expFuture;

          let finalTier = backendTier;
          if (
            storedTier &&
            TIER_RANK[storedTier] > TIER_RANK[backendTier] &&
            backendHasActiveSub
          ) {
            // Cas (a) : webhook /set-tier raté juste après checkout. Garder local + retry.
            finalTier = storedTier;
            console.warn(
              `[AUTH] ⚠️ tier conflict (recoverable): backend=${backendTier} sans sub-tier en DB ` +
              `mais subscription_period=${prof.subscription_period} active → keeping local (${finalTier}) ` +
              `+ resync /set-tier`
            );
            try {
              fetch(`${AWS_CONFIG.apiUrl}/set-tier`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                  tier:        finalTier,
                  period:      prof.subscription_period || 'monthly',
                  platform:    'resync',
                  payment_ref: `resync_${Date.now()}`,
                }),
              }).catch(() => {});
            } catch {}
          } else if (storedTier && storedTier !== backendTier) {
            // Cas (b) : le cache local est obsolète (backend dit free légitimement,
            // ou tier différent sans sub active). On purge pour ne plus avoir ce
            // conflit au prochain reload.
            console.log(
              `[AUTH] tier cache purged: backend=${backendTier}, local=${storedTier} → ` +
              `trusting backend (subscription_period=${prof?.subscription_period || 'null'}, ` +
              `cancelled=${!!prof?.subscription_cancelled}, expFuture=${expFuture})`
            );
            try {
              if (AsyncStorageRef) {
                await AsyncStorageRef.removeItem(`outfit_tier_${userId}`);
              }
            } catch {}
          }
          const tier = finalTier;

          // ═══ v1.7.80 : Fallback en Free si premium_expires_at est dépassé ═══
          // Garde-fou côté client : si la date d'expiration est passée mais que
          // le backend renvoie encore un tier payant (cas où /sync-subscription
          // n'a pas encore tourné), on force le tier à 'free' pour que l'UI
          // reflète la réalité immédiatement. Le backend sera resync au
          // prochain set-tier ou via le cron quotidien.
          let effectiveTier = tier;
          const expAt = prof?.premium_expires_at;
          if (expAt && tier !== 'free') {
            try {
              const expDate = new Date(expAt);
              if (!isNaN(expDate.getTime()) && expDate.getTime() < Date.now()) {
                console.warn(`[AUTH] ⚠️ premium_expires_at dépassé (${expAt}) → fallback en 'free' (était ${tier})`);
                effectiveTier = 'free';
                // Re-sync DB pour officialiser le passage en free
                try {
                  fetch(`${AWS_CONFIG.apiUrl}/set-tier`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                      tier:        'free',
                      period:      'monthly',
                      platform:    'expiration',
                      payment_ref: `auto_expire_${Date.now()}`,
                    }),
                  }).catch(() => {});
                  // Update cache local
                  try {
                    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
                    await AsyncStorage.setItem(`outfit_tier_${userId}`, 'free');
                  } catch {}
                } catch {}
              }
            } catch (e) {
              console.warn('[AUTH] expiration check error:', e?.message);
            }
          }

          // ═══ FIX 2026-04 : hydrater user avec les champs DB ═══
          // buildUser ne voit que les claims JWT qui peuvent ne pas contenir
          // birth_date, gender, etc. (champs remplis via onboarding et
          // stockés uniquement en DB). On merge ici pour que le user en
          // context ait toutes les infos → évite que l'onboarding se
          // redéclenche à chaque reconnexion.
          setter(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tier: effectiveTier,
              // Ne remplacer que si la DB a une valeur (sinon garder JWT)
              firstName: prof.first_name || prev.firstName,
              lastName:  prof.last_name  || prev.lastName,
              name:      prof.name       || prev.name,
              gender:    prof.gender     || prev.gender,
              // birth_date arrive de Postgres DATE — peut être en format
              // 'YYYY-MM-DD' (string) ou 'YYYY-MM-DDTHH:mm:ss.sssZ' (Date
              // JSON-stringifié) selon le driver pg. On normalise à 'YYYY-MM-DD'
              // pour que tous les consumers (CompleteProfileScreen validator,
              // ProfileScreen affichage) reçoivent un format stable.
              birthDate: (function(){
                const v = prof.birth_date;
                if (!v) return prev.birthDate;
                if (typeof v === 'string') {
                  const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
                  if (m) return m[1];
                }
                const d = v instanceof Date ? v : new Date(v);
                if (isNaN(d.getTime())) return prev.birthDate;
                const y = d.getFullYear();
                const mo = String(d.getMonth() + 1).padStart(2, '0');
                const da = String(d.getDate()).padStart(2, '0');
                return `${y}-${mo}-${da}`;
              })(),
              email:     prof.email      || prev.email,
              avatar:    prof.avatar_url || prev.avatar,
              // ═══ Champs morpho/sizes/locale ajoutés 2026-04 ═══
              // Hydratés depuis la DB pour que App.js puisse décider si
              // l'onboarding doit s'afficher (cas d'un user existant qui
              // n'a pas encore renseigné ces champs).
              height_cm:       prof.height_cm  != null ? Number(prof.height_cm) : prev.height_cm,
              weight_kg:       prof.weight_kg  != null ? Number(prof.weight_kg) : prev.weight_kg,
              silhouette:      prof.silhouette || prev.silhouette,
              clothing_sizes:  prof.clothing_sizes && typeof prof.clothing_sizes === 'object'
                                 ? prof.clothing_sizes
                                 : (prev.clothing_sizes || {}),
              preferred_locale: prof.preferred_locale || prev.preferred_locale,
              // ═══ Dates abonnement (pour affichage dans Profil) ═══
              // Postgres TIMESTAMPTZ peut arriver en string ISO ou Date.
              // On les normalise en strings ISO pour utilisation côté UI.
              premiumSince:     prof.premium_since      ? new Date(prof.premium_since).toISOString()      : (prev.premiumSince || null),
              premiumExpiresAt: prof.premium_expires_at ? new Date(prof.premium_expires_at).toISOString() : (prev.premiumExpiresAt || null),
              subscriptionPeriod: prof.subscription_period || prev.subscriptionPeriod || null,  // 'monthly' | 'yearly'
              subscriptionCancelled: !!prof.subscription_cancelled,
              // Crédit Mytenue — abo « offert par crédit » (comp serveur, sans renouvellement auto)
              isCreditComp:        !!prof.is_credit_comp,
              creditCompExpiresAt: prof.credit_comp_expires_at ? new Date(prof.credit_comp_expires_at).toISOString() : null,
              // v1.12.665 — Pending downgrade/switch info (monthly→yearly prepaid)
              subscriptionPendingDowngradeTier:   prof.pending_downgrade_tier   || null,
              subscriptionPendingDowngradePeriod: prof.pending_downgrade_period || null,
              subscriptionPendingDowngradeAt:     prof.pending_downgrade_at     ? new Date(prof.pending_downgrade_at).toISOString() : null,
              // ═══ Flag is_new_user du backend ═══
              // - true : compte créé à l'instant par ce GET /profile (jamais vu avant)
              // - false/undefined : compte déjà en base (l'user existait déjà)
              // App.js s'en sert pour décider s'il faut afficher l'onboarding.
              // v1.12.928 — Si PUT /profile a déjà posé isNewUser=true (signup juste avant),
              // on le garde. Sinon le GET /profile suivant (parfois loadé avant que la DB
              // soit cohérente) repasserait à false → onboarding raté.
              isNewUser: prev?.isNewUser === true ? true : !!prof.is_new_user,
              // ═══ Flag pour que App.js sache quand on peut évaluer
              // needsProfileCompletion. Sans ce flag, l'onboarding flash
              // apparaît une demi-seconde à chaque démarrage pendant que
              // le fetch /profile est en cours (avant hydratation).
              profileHydrated: true,
            };
          });
          profileLoaded = true;
          // Persister le tier en cache local — backend = source de vérité.
          // v1.13.21 — FIX bug tier Gold fantôme : on persiste effectiveTier (valeur
          // APRÈS fallback expiration), PAS `tier` brut qui pouvait rester payant
          // alors que l'abonnement était expiré → l'app affichait 500 slots / 40
          // try-ons fantômes. Et si free → PURGE TOTALE (tier + plan + timestamp)
          // au lieu d'écrire un timestamp « frais » qui faisait croire getTier() à
          // une réactivation récente (l'échappatoire ressuscitait alors le Gold).
          try {
            if (!effectiveTier || effectiveTier === 'free') {
              // v1.13.94 — Lire le tier caché AVANT la purge pour savoir si c'est
              //   un VRAI passage à free. Sinon (bug) : à CHAQUE chargement de profil
              //   d'un user déjà free, on ré-émettait tier_changed{free} → les écrans
              //   font closePremium() → le paywall "abonnement/add-ons" qui venait de
              //   s'ouvrir (arrivée Dressing/Looks, clic +/Ajouter quand quota IA
              //   épuisé) se FERMAIT tout seul. Cross-plateforme (web + app).
              let _prevCachedTier = null;
              try {
                const AS0 = (await import('@react-native-async-storage/async-storage')).default;
                _prevCachedTier = await AS0.getItem(`outfit_tier_${userId}`);
              } catch (_) {}
              const { purgeTierCache } = require('../services/PremiumService');
              await purgeTierCache(userId);
              // v1.13.24 — PURGE COMPLÈTE des caches Gold périmés.
              // Plusieurs caches locaux survivaient au passage free (et même au
              // logout) → l'app affichait encore Gold : 500 slots, 41 try-ons,
              // « Espace Max +250 actif » dans les add-ons, plan payant.
              //   - outfit_subscription_<uid> : snapshot de la sub (gold annulée)
              //     re-caché par fetchSubscription → relu par getCachedSubscription.
              //   - outfit_premium_expires_<uid> : date d'expiration payante.
              // Le backend étant autoritaire (free), on les efface tous.
              try {
                const AS = (await import('@react-native-async-storage/async-storage')).default;
                await AS.multiRemove([
                  `outfit_subscription_${userId}`,
                  `outfit_premium_expires_${userId}`,
                ]);
              } catch (_) {}
              // Notifier les écrans (Wardrobe/TryOn/Catalog/Profil/Add-ons) pour qu'ils
              // RECALCULENT leurs limites/abos immédiatement (pas au prochain lancement).
              // N'émettre QUE si l'user n'était pas DÉJÀ free (vrai downgrade).
              if (_prevCachedTier && _prevCachedTier !== 'free') {
                try {
                  const { DeviceEventEmitter } = require('react-native');
                  DeviceEventEmitter.emit('tier_changed', { tier: 'free' });
                  DeviceEventEmitter.emit('subscription_changed', { tier: 'free' });
                } catch (_) {}
              }
            } else {
              const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
              await AsyncStorage.setItem(`outfit_tier_${userId}`, effectiveTier);
              await AsyncStorage.setItem(`outfit_tier_updated_${userId}`, String(Date.now()));
            }
          } catch (_) {}
          console.log('[AUTH] Profile hydrated from DB:', {
            tier,
            firstName:  prof.first_name,
            lastName:   prof.last_name,
            gender:     prof.gender,
            birthDate:  prof.birth_date,
            email:      prof.email,
            height_cm:  prof.height_cm,
            weight_kg:  prof.weight_kg,
            silhouette: prof.silhouette,
            sizesKeys:  prof.clothing_sizes ? Object.keys(prof.clothing_sizes).length : 0,
          });
        }
      } catch(e) {
        console.warn('[AUTH] profile tier fetch failed:', e.message);
      }
      // Si le fetch a échoué (réseau HS), on marque quand même hydraté
      // pour ne pas bloquer indéfiniment sur le splash → fallback sur les
      // données JWT. Si des champs manquent, l'onboarding apparaîtra, c'est
      // le comportement actuel à défaut de meilleur choix.
      if (!profileLoaded) {
        setter(prev => prev ? { ...prev, profileHydrated: true } : prev);
        console.warn('[AUTH] Profile hydration failed — marked as hydrated to unblock UI');
      }

      // Puis synchroniser avec le serveur en arrière-plan
      const sub = await fetchSubscription(userId, accessToken);
      if (!sub) return;
      let plan = 'free';
      if (sub.status === 'active')    plan = sub.period || 'monthly';
      if (sub.status === 'cancelled') plan = 'cancelled';
      setter(prev => prev ? { ...prev, plan } : prev);
      // Sauvegarder localement pour que checkLimit fonctionne sans réseau
      try {
        await import('@react-native-async-storage/async-storage').then(m =>
          m.default.setItem(`outfit_plan_${userId}`, plan)
        );
      } catch {}
      console.log('[AUTH] Subscription synced:', sub.status, plan, 'user:', userId);
    } catch(e) {
      console.warn('[AUTH] loadSubscription error:', e.message);
    }
  }

  /* ── Google Sign In ──────────────────────────────────── */
  const signInWithGoogle = useCallback(async (googleUserInfo) => {
    setLoading(true); setError(null);
    try {
      if (!IS_AWS_CONFIGURED) {
        // Demo mode
        const u = {
          ...DEMO_USER,
          name:     googleUserInfo?.name    || DEMO_USER.name,
          email:    googleUserInfo?.email   || DEMO_USER.email,
          avatar:   googleUserInfo?.picture || null,
          initials: initials(googleUserInfo?.name || DEMO_USER.name),
          provider: 'google',
        };
        setUser(u);
        return { success: true, user: u };
      }

      // Production: tokens come from Cognito Hosted UI
      const { accessToken, idToken, refreshToken } = googleUserInfo;

      await saveTokens({
        AccessToken:  accessToken,
        IdToken:      idToken,
        RefreshToken: refreshToken,
      }, 'google');

      // Decode id_token
      const payload = decodeJwt(idToken || accessToken);

      // Use data passed from LoginScreen (already fetched from /oauth2/userInfo)
      const { name: passedName, email: passedEmail, picture: passedPicture,
              firstName: passedFirst, lastName: passedLast } = googleUserInfo;

      // v1.12.488 — Sauvegarde email Google pour re-login (login_hint évite authuser=2 → 400)
      try {
        if (typeof window !== 'undefined' && window.localStorage && passedEmail) {
          window.localStorage.setItem('last_google_email', passedEmail);
        }
      } catch {}

      const firstName = passedFirst  || payload.given_name  || '';
      const lastName  = passedLast   || payload.family_name || '';
      const fullName  = passedName   || payload.name        || `${firstName} ${lastName}`.trim() || passedEmail || payload.email || '';
      const email     = passedEmail  || payload.email       || '';
      const picture   = passedPicture|| payload.picture     || null;
      // ═══ Locale Google ═══
      // Google fournit le claim `locale` (ex: 'fr', 'fr-FR', 'en-US') dans le
      // ID token. On le récupère et on l'enverra dans /profile pour que
      // l'app s'affiche dans la bonne langue.
      const googleLocale = googleUserInfo?.locale || payload.locale || null;
      const preferredLocale = normalizeLocale(googleLocale);

      const u = {
        ...buildUser(payload, accessToken),
        name:       fullName,
        firstName,
        lastName,
        email,
        avatar:     picture,
        initials:   initials(fullName),
        provider:   'google',
        ...(preferredLocale ? { preferred_locale: preferredLocale } : {}),
      };

      setUser(u);

      // Upsert in DB with full Google profile
      const dbProfile = await upsertProfileInDB(accessToken, {
        name:       fullName,
        first_name: firstName,
        last_name:  lastName,
        email,
        avatar_url: picture,
        provider:   'google',
        ...(preferredLocale ? { preferred_locale: preferredLocale } : {}),
      });

      // Merge DB data (member since, login count, etc.)
      const finalUser = {
        ...u,
        memberSince:  dbProfile?.first_login_at || dbProfile?.created_at || new Date().toISOString(),
        loginCount:   dbProfile?.login_count    || 1,
        isNewUser:    dbProfile?.is_new_user     || false,
        since:        formatMemberSince(dbProfile?.first_login_at || new Date().toISOString()),
        tier:         dbProfile?.tier || 'free',
      };
      setUser(finalUser);

      // ═══ FIX 2026-04-26 : hydrater les champs DB (gender, birth_date, morpho, sizes) ═══
      // upsertProfileInDB renvoie le profil DB mais on ne l'utilise que pour
      // memberSince/loginCount/tier. Les champs gender/birthDate/morpho viennent
      // d'un GET /profile complet → on l'appelle explicitement ici.
      // Sans ça, ProfileScreen affichait "COMPLÉTER LE PROFIL" même pour
      // un user dont le profil DB est complet.
      // ═══ Important : try/catch défensif autour ═══
      // L'appel est asynchrone et non-bloquant, mais si une dépendance interne
      // (cache local AsyncStorage corrompu, etc.) throw, ça pourrait remonter.
      try {
        loadSubscriptionForUser(u.id, accessToken, setUser);
      } catch (hydrErr) {
        console.warn('[AUTH] hydration trigger failed (non-bloquant):', hydrErr?.message);
      }

      // v1.12.371 — GA4: login (method=google)
      try {
        const { trackEvent, setUserId } = require('../services/AnalyticsService');
        trackEvent('login', { method: 'google', is_new_user: !!finalUser?.isNewUser });
        // Conversion unifiée Google Ads : nouveau compte → sign_up (tous modes).
        if (finalUser?.isNewUser) trackEvent('sign_up', { method: 'google' });
        if (finalUser?.id) setUserId(finalUser.id);
      } catch {}

      return { success: true, user: finalUser };
    } catch (e) {
      setError(e.message);
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('login_failed', { method: 'google', reason: (e?.message || 'unknown').slice(0, 100) });
      } catch {}
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Apple Sign In ───────────────────────────────────── */
  //
  // Flux (TestFlight / App Store) :
  //   1. LoginScreen appelle AppleAuthentication.signInAsync() → bouton natif iOS
  //   2. Apple renvoie { identityToken, authorizationCode, user, email?, fullName? }
  //      ⚠️ email + fullName ne sont fournis QUE lors du TOUT PREMIER sign-in.
  //         Aux connexions suivantes ces champs sont null → il faut les cacher DB.
  //   3. On POST l'identityToken + les infos user à notre Lambda endpoint
  //      /auth/apple qui :
  //        - valide la signature du JWT avec les JWKs Apple
  //        - vérifie l'audience (= bundleId) et l'issuer
  //        - crée/retrouve l'user dans Cognito (AdminCreateUser si inexistant)
  //        - renvoie un set de tokens Cognito (AccessToken, IdToken, RefreshToken)
  //   4. On save tokens en SecureStore + upsert profil DB (comme Google)
  //
  const signInWithApple = useCallback(async (appleCredential) => {
    setLoading(true); setError(null);
    try {
      if (!IS_AWS_CONFIGURED) {
        // ── DEMO MODE ────────────────────────────────────────
        const fn   = appleCredential?.fullName;
        const name = fn ? `${fn.givenName || ''} ${fn.familyName || ''}`.trim() : DEMO_USER.name;
        const u    = {
          ...DEMO_USER,
          name:     name || DEMO_USER.name,
          email:    appleCredential?.email || DEMO_USER.email,
          initials: initials(name || DEMO_USER.name),
          provider: 'apple',
        };
        setUser(u);
        return { success: true, user: u };
      }

      // ── PRODUCTION ───────────────────────────────────────
      if (!appleCredential?.identityToken) {
        throw new Error('No identity token from Apple');
      }

      // POST identity token + user info à notre endpoint Lambda /auth/apple
      // qui fait le pont avec Cognito (cf. lambda/handler.js → handleAppleSignIn)
      const fn        = appleCredential.fullName;
      const firstName = fn?.givenName  || '';
      const lastName  = fn?.familyName || '';
      const fullName  = `${firstName} ${lastName}`.trim();

      const bridgeRes = await fetch(`${AWS_CONFIG.apiUrl}/auth/apple`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identityToken:     appleCredential.identityToken,
          authorizationCode: appleCredential.authorizationCode || null,
          user:              appleCredential.user,
          // email / fullName ne sont présents que le 1er sign-in :
          // le backend doit les persister à la création de l'user Cognito
          email:             appleCredential.email || null,
          firstName:         firstName || null,
          lastName:          lastName  || null,
          fullName:          fullName  || null,
        }),
      });

      if (!bridgeRes.ok) {
        const err = await bridgeRes.json().catch(() => ({}));
        throw new Error(err.message || `Apple bridge failed (${bridgeRes.status})`);
      }

      const { accessToken, idToken, refreshToken, email: srvEmail } = await bridgeRes.json();

      // Save tokens (format Cognito PascalCase pour saveTokens)
      await saveTokens({
        AccessToken:  accessToken,
        IdToken:      idToken,
        RefreshToken: refreshToken,
      }, 'apple');

      // Build user depuis le JWT
      const payload = decodeJwt(idToken || accessToken);
      const email   = srvEmail || appleCredential.email || payload.email || '';
      const name    = fullName || payload.name || email || 'Utilisateur Apple';
      // ═══ Locale Apple ═══
      // Apple ne renvoie pas de claim `locale` dans son JWT. On utilise donc
      // la locale système du device, qui correspond pour 99% des users à la
      // langue de leur Apple ID. On lit via expo-localization (SDK 54+ :
      // getLocales() retourne un array, l'ancienne prop Localization.locale
      // est dépréciée).
      let preferredLocale = null;
      try {
        const Localization = require('expo-localization');
        let sysLocale = null;
        if (Localization && typeof Localization.getLocales === 'function') {
          const arr = Localization.getLocales();
          if (Array.isArray(arr) && arr.length > 0) {
            sysLocale = arr[0].languageTag || arr[0].languageCode;
          }
        }
        // Fallback ancien API au cas où on tourne sur SDK ancien
        if (!sysLocale && Localization) {
          sysLocale = Localization.locale || (Array.isArray(Localization.locales) ? Localization.locales[0] : null);
        }
        preferredLocale = normalizeLocale(sysLocale);
      } catch (_) {}

      const u = {
        ...buildUser(payload, accessToken),
        name,
        firstName: firstName || payload.given_name  || '',
        lastName:  lastName  || payload.family_name || '',
        email,
        initials:  initials(name),
        provider:  'apple',
        ...(preferredLocale ? { preferred_locale: preferredLocale } : {}),
      };
      setUser(u);

      // Upsert profil en DB (même pattern que Google)
      const dbProfile = await upsertProfileInDB(accessToken, {
        name,
        first_name: u.firstName,
        last_name:  u.lastName,
        email,
        provider:   'apple',
        ...(preferredLocale ? { preferred_locale: preferredLocale } : {}),
      });

      const finalUser = {
        ...u,
        memberSince: dbProfile?.first_login_at || dbProfile?.created_at || new Date().toISOString(),
        loginCount:  dbProfile?.login_count    || 1,
        isNewUser:   dbProfile?.is_new_user     || false,
        since:       formatMemberSince(dbProfile?.first_login_at || new Date().toISOString()),
        tier:        dbProfile?.tier || 'free',
      };
      setUser(finalUser);

      // ═══ FIX 2026-04-26 : hydrater les champs DB (gender, birth_date, morpho, sizes) ═══
      // Idem signInWithGoogle : sans cet appel, gender/birthDate/morpho restent
      // null après login Apple → ProfileScreen affiche "COMPLÉTER LE PROFIL"
      // alors que les infos sont en DB.
      try {
        loadSubscriptionForUser(u.id, accessToken, setUser);
      } catch (hydrErr) {
        console.warn('[AUTH] hydration trigger failed (non-bloquant):', hydrErr?.message);
      }

      // v1.12.371 — GA4: login (method=apple)
      try {
        const { trackEvent, setUserId } = require('../services/AnalyticsService');
        trackEvent('login', { method: 'apple', is_new_user: !!finalUser?.isNewUser });
        // Conversion unifiée Google Ads : nouveau compte → sign_up (tous modes).
        if (finalUser?.isNewUser) trackEvent('sign_up', { method: 'apple' });
        if (finalUser?.id) setUserId(finalUser.id);
      } catch {}

      return { success: true, user: finalUser };
    } catch (e) {
      console.error('[AUTH] Apple sign-in error:', e);
      setError(e.message);
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('login_failed', { method: 'apple', reason: (e?.message || 'unknown').slice(0, 100) });
      } catch {}
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Email / Password (Phase 2) ───────────────────────────
   * Phase 2 : email verification activée via SES (no-reply@mytenue.com).
   * Flow signup :
   *   1. signUpWithEmail → Lambda /auth/email-signup (envoie code par email)
   *   2. UI redirige vers ConfirmEmailScreen avec l'email en param
   *   3. confirmEmail(email, code) → Lambda /auth/email-confirm
   *   4. Sur succès → user peut se connecter via signInWithEmail
   * Flow reset password :
   *   1. requestPasswordReset(email) → Lambda /auth/email-forgot (code envoyé)
   *   2. confirmPasswordReset(email, code, newPassword) → /auth/email-reset
   */
  const signUpWithEmail = useCallback(async ({ email, password, firstName, lastName }) => {
    setLoading(true); setError(null);
    try {
      if (!IS_AWS_CONFIGURED) {
        // Mode démo : skip code de vérif, log direct
        const u = {
          ...DEMO_USER,
          email,
          name: `${firstName || ''} ${lastName || ''}`.trim() || email,
          provider: 'email',
        };
        setUser(u);
        return { success: true, user: u, needsConfirmation: false };
      }

      // v1.12.268 — passe lang en clientMetadata pour Cognito custom message
      let _lang = 'fr';
      try { const { getCurrentLang } = require('../i18n'); _lang = getCurrentLang() || 'fr'; } catch {}
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-signup`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password, firstName, lastName, lang: _lang }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Échec création compte (${res.status})`);
      }
      // v1.12.371 — GA4: sign_up (method=email)
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('sign_up', { method: 'email', needs_confirmation: !!data.needsConfirmation });
      } catch {}
      // Phase 2 : pas de tokens immédiats, l'user doit vérifier son email.
      return {
        success: true,
        needsConfirmation: !!data.needsConfirmation,
        email: data.email || email,
        firstName: data.firstName || firstName,
        lastName:  data.lastName  || lastName,
      };
    } catch (e) {
      console.error('[AUTH] email signup error:', e);
      setError(e.message);
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('sign_up_failed', { method: 'email', reason: (e?.message || 'unknown').slice(0, 100) });
      } catch {}
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmEmail = useCallback(async ({ email, code }) => {
    setLoading(true); setError(null);
    try {
      if (!IS_AWS_CONFIGURED) return { success: true };
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-confirm`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Vérification échouée (${res.status})`);
      return { success: true, email: data.email || email };
    } catch (e) {
      console.error('[AUTH] confirm email error:', e);
      setError(e.message);
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const resendConfirmCode = useCallback(async ({ email }) => {
    try {
      if (!IS_AWS_CONFIGURED) return { success: true };
      let _l = 'fr'; try { const { getCurrentLang } = require('../i18n'); _l = getCurrentLang() || 'fr'; } catch {}
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-resend-code`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, lang: _l }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Échec renvoi code (${res.status})`);
      return { success: true };
    } catch (e) {
      console.error('[AUTH] resend code error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  const requestPasswordReset = useCallback(async ({ email }) => {
    try {
      if (!IS_AWS_CONFIGURED) return { success: true };
      let _l = 'fr'; try { const { getCurrentLang } = require('../i18n'); _l = getCurrentLang() || 'fr'; } catch {}
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-forgot`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, lang: _l }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Échec envoi code reset (${res.status})`);
      // v1.12.930 — needsEmailVerification : forward au caller pour router vers ConfirmEmail
      if (data.needsEmailVerification) {
        return { success: false, needsEmailVerification: true, email, message: data.message };
      }
      // v1.12.371 — GA4: password_reset_requested
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('password_reset_requested', { method: 'email' });
      } catch {}
      return { success: true };
    } catch (e) {
      console.error('[AUTH] forgot password error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  // v1.12.930 — Pré-auth email verification (pas de JWT)
  const sendEmailVerifyCode = useCallback(async ({ email }) => {
    try {
      if (!IS_AWS_CONFIGURED) return { success: true };
      let _l = 'fr'; try { const { getCurrentLang } = require('../i18n'); _l = getCurrentLang() || 'fr'; } catch {}
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-verify-send`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, lang: _l }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Échec envoi code (${res.status})`);
      return { success: true, alreadyVerified: !!data.alreadyVerified };
    } catch (e) {
      console.error('[AUTH] sendEmailVerifyCode error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  const confirmEmailVerifyCode = useCallback(async ({ email, code }) => {
    try {
      if (!IS_AWS_CONFIGURED) return { success: true };
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-verify-confirm`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Code invalide (${res.status})`);
      return { success: true, email };
    } catch (e) {
      console.error('[AUTH] confirmEmailVerifyCode error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  const confirmPasswordReset = useCallback(async ({ email, code, newPassword }) => {
    try {
      if (!IS_AWS_CONFIGURED) return { success: true };
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-reset`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, code, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Échec reset (${res.status})`);
      // v1.12.371 — GA4: password_reset_completed
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('password_reset_completed', { method: 'email' });
      } catch {}
      return { success: true };
    } catch (e) {
      console.error('[AUTH] confirm password reset error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  const signInWithEmail = useCallback(async ({ email, password }) => {
    setLoading(true); setError(null);
    // v1.12.268 — Persistent debug log dans localStorage pour diagnostic Safari mobile
    const _dbg = (msg, data = {}) => {
      try {
        const log = JSON.parse(localStorage.getItem('mt_signin_debug') || '[]');
        log.push({ t: new Date().toISOString(), msg, ...data });
        localStorage.setItem('mt_signin_debug', JSON.stringify(log.slice(-30)));
      } catch {}
      console.log('[SIGNIN DEBUG]', msg, data);
    };
    _dbg('start', { email, hasPassword: !!password });
    try {
      if (!IS_AWS_CONFIGURED) {
        const u = { ...DEMO_USER, email, provider: 'email' };
        setUser(u);
        return { success: true, user: u };
      }

      // v1.12.930 — passe lang pour template email vérification
      let _lang = 'fr';
      try { const { getCurrentLang } = require('../i18n'); _lang = getCurrentLang() || 'fr'; } catch {}
      const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/email-signin`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password, lang: _lang }),
      });
      _dbg('fetch_response', { status: res.status, ok: res.ok });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        _dbg('error_body', { err });
        throw new Error(err.message || `Connexion impossible (${res.status})`);
      }
      const responseData = await res.json();

      // v1.12.930 — Cas email non vérifié : bloquer login + déclencher flow vérif
      if (responseData.needsEmailVerification) {
        _dbg('needs_email_verification', { email });
        return { success: false, needsEmailVerification: true, email, message: responseData.message };
      }

      const { accessToken, idToken, refreshToken } = responseData;

      await saveTokens(
        { AccessToken: accessToken, IdToken: idToken, RefreshToken: refreshToken },
        'email',
      );
      const payload = decodeJwt(idToken || accessToken);
      const u = {
        ...buildUser(payload, accessToken),
        email:     payload.email || email,
        provider:  'email',
        initials:  initials(payload.name || email),
      };
      setUser(u);
      _dbg('success', { userId: u.id, email: u.email, hasName: !!u.name });
      try { loadSubscriptionForUser(u.id, accessToken, setUser); } catch {}
      // v1.12.371 — GA4: login (method=email)
      try {
        const { trackEvent, setUserId } = require('../services/AnalyticsService');
        trackEvent('login', { method: 'email' });
        if (u?.id) setUserId(u.id);
      } catch {}
      return { success: true, user: u };
    } catch (e) {
      console.error('[AUTH] email signin error:', e);
      _dbg('exception', { message: e.message, stack: (e.stack || '').slice(0, 200) });
      setError(e.message);
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('login_failed', { method: 'email', reason: (e?.message || 'unknown').slice(0, 100) });
      } catch {}
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Sign Out ────────────────────────────────────────── */
  const signOut = useCallback(async () => {
    // v1.12.624 — Log qui appelle signOut (bug "déconnexion brusque")
    try {
      console.warn('[AUTH] 🔍 signOut called from:', new Error('trace').stack?.split('\n').slice(1, 8).join('\n'));
    } catch {}
    // v1.12.371 — GA4: logout (fire-and-forget avant le cleanup async)
    try {
      const { trackEvent } = require('../services/AnalyticsService');
      trackEvent('logout', {});
    } catch {}
    if (IS_AWS_CONFIGURED) {
      const access = await SecureStore.getItemAsync('outfit_access').catch(() => null);
      if (access) {
        await cognitoCall('GlobalSignOut', { AccessToken: access }).catch(() => {});
      }

      // ═══ FIX v1.12.614 : nettoyage session Cognito Hosted UI ═══
      // Web : l'iframe vers /logout NE FONCTIONNAIT PAS (Cognito met
      // X-Frame-Options: deny → iframe bloquée → cookie jamais clear).
      // Résultat : au prochain Google login, Cognito réutilisait le cookie
      // qui pointait sur l'ancien sub (supprimé après delete) → 400.
      // Sur web on ne fait plus rien ici dans signOut normal — la session
      // cookie sera invalidée naturellement au prochain login. Pour le cas
      // post-DELETE compte où c'est critique, deleteAccount() force un
      // window.location.replace(logoutUrl) qui clear vraiment le cookie.
      // Native iOS/Android : WebBrowser fonctionne (pas de X-Frame-Options).
      try {
        const domain   = AWS_CONFIG.oauth?.domain;
        const clientId = AWS_CONFIG.userPoolClientId;
        if (domain && clientId && Platform.OS !== 'web') {
          const logoutUri = AWS_CONFIG.oauth?.redirectSignOut || AWS_CONFIG.oauth?.redirectSignIn || 'outfitapp://';
          const logoutUrl = `https://${domain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(logoutUri)}`;
          await WebBrowser.openAuthSessionAsync(logoutUrl, logoutUri, {
            preferEphemeralSession: false,
          }).catch(() => {});
        }
      } catch (e) {
        console.warn('[signOut] Cognito logout failed:', e.message);
      }

      await clearTokens();
    }
    // Effacer les préférences du jour : changement de compte = nouveau context
    try {
      const { clearSessionPrefs, setCachedPrefs } = require('../services/DailyPreferencesService');
      await clearSessionPrefs();
      setCachedPrefs(null);
    } catch {}
    // ═══ FIX 2026-04 : clean session RevenueCat ═══
    // logOut() remet RC en mode anonymous, et au prochain login on aura
    // un identifyUser propre. Sans ça, RC garderait l'identification du
    // user précédent → confusion entre comptes sur même device.
    try {
      const { logOutPurchases } = require('../services/IAPService');
      await logOutPurchases();
    } catch {}
    // Effacer le cache profil local du user déconnecté
    if (user?.id) await clearProfileCache(user.id).catch(() => {});
    // v1.13.25 — PURGE des caches premium PAR-UTILISATEUR au logout.
    // BUG : clearTokens ne touchait que des clés globales → les caches tier/abo
    // par-uid SURVIVAIENT à la déconnexion → un compte repassé free continuait
    // d'afficher Gold (500 slots, 41 try-ons, « Espace Max +250 actif ») même
    // après logout/login. On les efface explicitement.
    if (user?.id) {
      try {
        const AS = (await import('@react-native-async-storage/async-storage')).default;
        await AS.multiRemove([
          `outfit_tier_${user.id}`,
          `outfit_plan_${user.id}`,
          `outfit_tier_updated_${user.id}`,
          `outfit_subscription_${user.id}`,
          `outfit_premium_expires_${user.id}`,
        ]);
      } catch (_) {}
    }
    setUser(null); setError(null);
  }, [user?.id]);

  // ═══ Suppression de compte (RGPD / App Store §5.1.1(v)) ═══
  // Appelle DELETE /profile côté backend (cascade SQL + best-effort S3/Cognito),
  // puis nettoie la session locale comme un signOut. À utiliser SEULEMENT après
  // une double confirmation utilisateur — l'opération est irréversible.
  // v1.12.897 — opts.force=true → bypass active_subscription warning (user a confirmé)
  const deleteAccount = useCallback(async (opts = {}) => {
    if (!IS_AWS_CONFIGURED) {
      // En mode démo, on simule juste un signOut
      await signOut();
      return { ok: true, mode: 'demo' };
    }
    let backendOk = false;
    try {
      const access = user?.accessToken
        || await SecureStore.getItemAsync('outfit_access').catch(() => null);
      if (!access) throw new Error('No access token');

      const url = `${AWS_CONFIG.apiUrl}/profile${opts.force ? '?force=1' : ''}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${access}` },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.warn('[deleteAccount] backend non-OK:', res.status, j);
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      // v1.12.897 — warning abonnement actif : ne PAS sign out, retourner au caller
      if (j.warning === 'active_subscription') {
        console.log('[deleteAccount] ⚠ active subscription — caller must confirm or cancel sub first');
        return { ok: false, warning: 'active_subscription', message: j.message };
      }
      backendOk = true;
      console.log('[deleteAccount] ✓ backend cleared');
    } catch (e) {
      console.error('[deleteAccount] backend error:', e?.message);
      // On rethrow pour que l'UI puisse alerter l'user — la suppression DB
      // n'a pas eu lieu, on ne veut pas faire signOut dans ce cas.
      throw e;
    }

    // v1.12.268 — Force clear COMPLET avant signOut (web: localStorage tokens persistaient
    // après delete → user reconnectait avec ancien JWT sur compte supprimé).
    try { await clearTokens(); } catch (_) {}
    try {
      if (typeof localStorage !== 'undefined') {
        ['outfit_access','outfit_id','outfit_refresh','outfit_provider','outfit_user','mytenue_profile_cache'].forEach(function(k){ try { localStorage.removeItem(k); } catch(_) {} });
      }
    } catch(_) {}
    setUser(null);
    try {
      await signOut();
    } catch (e) {
      console.warn('[deleteAccount] signOut after delete failed:', e?.message);
    }

    // v1.12.614 — Web : hard redirect vers Cognito /logout pour clear le
    // cookie de session Hosted UI. Sans ça, au prochain Google login,
    // Cognito retourne un token pointant sur le sub supprimé → 400.
    // L'user voit un bref flash Cognito puis revient sur / propre, prêt
    // à se reconnecter avec un nouveau profil.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const domain   = AWS_CONFIG.oauth?.domain;
        const clientId = AWS_CONFIG.userPoolClientId;
        if (domain && clientId) {
          const logoutUri = `${window.location.origin}/`;
          const logoutUrl = `https://${domain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(logoutUri)}`;
          console.log('[deleteAccount] 🔄 hard redirect Cognito logout to clear session cookie');
          window.location.replace(logoutUrl);
          // pas de return ici : la page va naviguer, le code après ne s'exécute pas
        }
      } catch (e) {
        console.warn('[deleteAccount] Cognito logout redirect failed:', e?.message);
      }
    }
    return { ok: true, backendOk };
  }, [user, signOut]);

  const updateUserProfile = useCallback(async (updates) => {
    if (!user) return;
    setUser(prev => ({ ...prev, ...updates }));
    if (IS_AWS_CONFIGURED && user.accessToken) {
      await fetch(`${AWS_CONFIG.apiUrl}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify(updates),
      }).catch(() => {});
    }
  }, [user]);

  // ═══ Force un re-fetch du profil DB pour rafraîchir tier/expiration ═══
  // À appeler après une mutation côté DB (upgrade/downgrade tier, achat,
  // expiration manuelle…) pour synchroniser le contexte avec la nouvelle
  // valeur de premium_expires_at, tier, etc.
  const reloadProfile = useCallback(async () => {
    if (!user?.id) return;
    const token = user?.accessToken || await SecureStore.getItemAsync('outfit_access').catch(() => null);
    if (!token) return;
    return loadSubscriptionForUser(user.id, token, setUser);
  }, [user?.id, user?.accessToken]);

  // ═══ v1.12.945 — Refresh tier après achat/changement de formule IAP ═══
  // Après un achat RevenueCat (purchaseSubscription émet 'subscription_changed'),
  // le tier réel met quelques secondes à se propager (webhook RC → DB). On
  // rafraîchit le profil depuis le backend (source de vérité) avec plusieurs
  // retries pour que tier + limites (try-on, wardrobe, IA…) reflètent la nouvelle
  // formule sans attendre un redémarrage. Avant : les écrans lisaient seulement
  // le cache RevenueCat local (souvent en retard) → limite restait "free".
  useEffect(() => {
    let timers = [];
    try {
      const em = require('react-native').DeviceEventEmitter;
      const sub = em.addListener('subscription_changed', () => {
        timers.forEach(clearTimeout);
        timers = [1000, 3000, 6000, 12000].map(ms =>
          setTimeout(() => { reloadProfile().catch(() => {}); }, ms)
        );
      });
      return () => { sub.remove(); timers.forEach(clearTimeout); };
    } catch (_) {}
  }, [reloadProfile]);

  // ═══ v1.12.945 — Persistance RGPD du consentement cookies en BD ═══
  // Le bandeau web (CookieConsent) écrit localStorage + émet 'cookie_consent_changed'.
  // On enregistre le choix côté serveur (table user_consents, consent_type='cookies')
  // dès qu'un user est identifié → preuve de consentement extractible (comme le 18+),
  // pas seulement en localStorage. Deux déclencheurs : (1) au login si un choix
  // localStorage existe et n'a pas encore été synchronisé pour cet user, (2) à chaque
  // changement via le bandeau.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    let sub;
    const recordCookies = async (choice) => {
      try {
        if (!choice || cancelled) return;
        const token = await getValidToken();
        if (!token || cancelled) return;
        const { recordConsent } = require('../services/ConsentService');
        await recordConsent({ authToken: token, consentType: 'cookies', metadata: { choice, surface: 'web' } });
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('outfit_cookie_consent_synced_uid', user.id);
          }
        } catch (_) {}
      } catch (_) {}
    };
    // (1) Sync unique du choix déjà stocké au login
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('outfit_cookie_consent');
        const syncedFor = window.localStorage.getItem('outfit_cookie_consent_synced_uid');
        if (stored && syncedFor !== user.id) recordCookies(stored);
      }
    } catch (_) {}
    // (2) Changement via le bandeau
    try {
      const em = require('react-native').DeviceEventEmitter;
      sub = em.addListener('cookie_consent_changed', (p) => recordCookies(p?.choice));
    } catch (_) {}
    return () => { cancelled = true; if (sub) sub.remove(); };
  }, [user?.id]);

  return (
    <AuthContext.Provider value={{
      user, loading, error,
      isAuthenticated:  !!user,
      isConfigured:     IS_AWS_CONFIGURED,
      signInWithGoogle, signInWithApple,
      signUpWithEmail,  signInWithEmail,
      confirmEmail,     resendConfirmCode,
      requestPasswordReset, confirmPasswordReset,
      sendEmailVerifyCode, confirmEmailVerifyCode,
      signOut,          deleteAccount,    updateUserProfile,
      getValidToken,    reloadProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
